/**
 * BillingSubscription - 2026-06-07 r-batch.
 *
 * Honest demo-safe Billing & Subscription page with a real plan grid.
 *
 *   - Current plan tile: Demo Mode
 *   - 5 plan cards: Explorer (Free) / Retail Trader ($49) / Pro Trader
 *     ($129) / Institutional Pro ($299) / Enterprise (Custom)
 *   - Every plan select button is DISABLED with an inline reason
 *     ("Available after live billing is enabled"). No alert(), no
 *     fake card numbers, no fake invoice ids, no fake billing
 *     address.
 *   - Plan-grid testids per spec:
 *       billing-plan-grid
 *       billing-plan-explorer
 *       billing-plan-retail-trader
 *       billing-plan-pro-trader
 *       billing-plan-institutional-pro
 *       billing-plan-enterprise
 *       billing-plan-disabled-reason
 */
import React from "react";
import { CreditCard, Lock, History as HistoryIcon, FileText, CheckCircle2 } from "lucide-react";
import useAccountSummary from "../../hooks/useAccountSummary";

const SectionHeader = ({ title, subtitle, icon: Icon }: { title: string; subtitle?: string; icon: any }) => (
  <div className="mb-6">
    <div className="flex items-center gap-2 mb-1">
      <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center text-emerald-600">
        <Icon size={18} />
      </div>
      <h3 className="text-lg font-black text-[var(--app-text)] uppercase italic tracking-tight">{title}</h3>
    </div>
    {subtitle && <p className="text-xs text-[var(--muted-text)] font-medium ml-10">{subtitle}</p>}
  </div>
);

const EmptyTile = ({ label, body, icon: Icon, testid }: { label: string; body: string; icon: any; testid: string }) => (
  <div data-testid={testid} className="bg-[var(--card-bg)] border border-dashed border-[var(--card-border)] rounded-2xl p-6">
    <div className="flex items-center gap-3 mb-2">
      <Icon size={16} className="text-[var(--muted-text)]" />
      <span className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest">{label}</span>
    </div>
    <div className="text-sm font-bold text-[var(--app-text)]">{body}</div>
  </div>
);

interface PlanCard {
  testid: string;
  name: string;
  priceLine: string;
  bullets: string[];
  highlight?: boolean;
}

const PLANS: PlanCard[] = [
  {
    testid: "billing-plan-explorer",
    name: "Explorer",
    priceLine: "Free",
    bullets: [
      "Demo paper account",
      "Market Pulse + Macro calendar",
      "Council insights (read-only)",
    ],
  },
  {
    testid: "billing-plan-retail-trader",
    name: "Retail Trader",
    priceLine: "$49 / month",
    bullets: [
      "Connect 1 paper brokerage",
      "Strategy alerts",
      "Daily Council briefings",
    ],
  },
  {
    testid: "billing-plan-pro-trader",
    name: "Pro Trader",
    priceLine: "$129 / month",
    bullets: [
      "Connect up to 3 paper brokerages",
      "Strategy builder",
      "Live signal feed",
    ],
    highlight: true,
  },
  {
    testid: "billing-plan-institutional-pro",
    name: "Institutional Pro",
    priceLine: "$299 / month",
    bullets: [
      "Multi-account portfolio",
      "Priority Council review",
      "Custom risk overlays",
    ],
  },
  {
    testid: "billing-plan-enterprise",
    name: "Enterprise",
    priceLine: "Custom",
    bullets: [
      "Dedicated account team",
      "SLA + onboarding",
      "Custom integrations",
    ],
  },
];

export default function BillingSubscription() {
  const summary = useAccountSummary();
  const isDemo = summary.demoProfile.demoMode || true;

  return (
    <div data-testid="billing-page" className="space-y-6">
      {/* Plan header + demo state */}
      <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-6 shadow-sm">
        <SectionHeader
          title="Billing & Subscription"
          subtitle="Billing is provided by the Adversyn platform team and is not yet exposed via the user API."
          icon={CreditCard}
        />

        <div
          data-testid="billing-plan-demo"
          className="mb-4 p-4 rounded-xl border border-[var(--card-border)] bg-[var(--input-bg)] flex items-center justify-between"
        >
          <div>
            <div className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest mb-1">Current Plan</div>
            <div className="text-base font-black text-[var(--app-text)] italic uppercase">Demo Mode</div>
          </div>
          <span className="text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded-md border border-emerald-500/30 bg-emerald-500/10 text-emerald-700">
            No charge
          </span>
        </div>

        <div
          data-testid="billing-demo-state"
          className="p-4 rounded-xl border border-amber-500/30 bg-amber-500/10 text-xs font-bold text-amber-700 uppercase tracking-widest"
        >
          {isDemo ? "Billing inactive for demo accounts" : "Plan data unavailable. Billing service not connected."}
        </div>
      </div>

      {/* Plan grid */}
      <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-6 shadow-sm">
        <SectionHeader
          title="Available plans"
          subtitle="Live billing is not enabled in demo. Plan selection becomes active once the billing service is connected."
          icon={CreditCard}
        />
        <div data-testid="billing-plan-grid" className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
          {PLANS.map((plan) => (
            <div
              key={plan.testid}
              data-testid={plan.testid}
              className={
                "p-5 rounded-2xl border bg-[var(--input-bg)] flex flex-col gap-3 " +
                (plan.highlight
                  ? "border-emerald-500/40 ring-1 ring-emerald-500/30"
                  : "border-[var(--card-border)]")
              }
            >
              <div>
                <div className="text-sm font-black text-[var(--app-text)] uppercase italic tracking-tight">{plan.name}</div>
                <div className="mt-1 text-base font-black text-[var(--app-text)]">{plan.priceLine}</div>
              </div>
              <ul className="space-y-1 text-xs text-[var(--muted-text)] flex-1">
                {plan.bullets.map((b) => (
                  <li key={b} className="flex items-start gap-2">
                    <CheckCircle2 size={12} className="text-emerald-500 mt-0.5 shrink-0" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
              <button
                type="button"
                disabled
                aria-disabled
                title="Available after live billing is enabled"
                className="w-full py-2 rounded-lg border border-[var(--card-border)] bg-[var(--card-bg)] text-[10px] font-black uppercase tracking-widest text-[var(--muted-text)] opacity-60 cursor-not-allowed"
              >
                Select plan
              </button>
              <div
                data-testid="billing-plan-disabled-reason"
                className="text-[10px] font-bold uppercase tracking-widest text-[var(--muted-text)] text-center"
              >
                Available after live billing is enabled
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Empty payment / invoices / address tiles */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <EmptyTile testid="billing-payment-empty" label="Payment method" body="No payment method on file" icon={CreditCard} />
        <EmptyTile testid="billing-invoices-empty" label="Invoices" body="No invoices available" icon={HistoryIcon} />
        <EmptyTile testid="billing-address-empty" label="Billing address" body="No billing address on file" icon={Lock} />
      </div>

      {/* Honesty note */}
      <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-6 shadow-sm">
        <div className="flex items-center gap-2 mb-3 text-[var(--muted-text)]">
          <FileText size={14} />
          <span className="text-[10px] font-black uppercase tracking-widest">Honesty note</span>
        </div>
        <p className="text-xs font-medium text-[var(--muted-text)] leading-relaxed">
          Adversyn does not collect or store billing information on demo
          accounts. Real billing controls (plan upgrades, invoices, payment
          method, billing address) appear here once a paid plan is provisioned
          and the billing service is connected. No mock plan, card, address,
          or invoice data is ever rendered.
        </p>
      </div>
    </div>
  );
}
